import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, UserRound } from 'lucide-react';
import { api } from '../../lib/api';

// "Usuarios do AppCel" — a tela que promove alguem a administrador do aplicativo
// (migration 0224). Vive dentro do menu ☰ da visao da loja.
//
// ⚠️ **Administrador AQUI nao e' admin do ERP.** A unica coisa que este interruptor
// decide e' se a porta "Entrar como loja" aparece no menu daquela pessoa. Quem passa
// por ela continua sendo decidido pelo login do sistema e pelo device-trust — por
// isso o rodape da tela diz isso em voz alta, em vez de deixar o leitor supor que
// esta distribuindo poder.
//
// ⚠️ **Nao ha promocao em massa e nao ha busca.** Sao 8 contas; filtro e selecao
// multipla aqui seriam peso sem pergunta correspondente. Quando a lista crescer, a
// busca entra — nao antes.

const COBRE = '#b45309';

interface UsuarioApp {
  id: number;
  nome: string;
  telefone: string;
  papel: 'usuario' | 'administrador';
  ativo: boolean;
  tem_ficha: boolean;
  criado_em: string;
  ultimo_acesso_em: string | null;
}

function telefoneBonito(t: string): string {
  const d = t.replace(/\D/g, '');
  const semDdi = d.startsWith('55') ? d.slice(2) : d;
  if (semDdi.length < 10) return t;
  const ddd = semDdi.slice(0, 2);
  const resto = semDdi.slice(2);
  const meio = resto.length === 9 ? 5 : 4;
  return `(${ddd}) ${resto.slice(0, meio)}-${resto.slice(meio)}`;
}

function quando(iso: string | null): string {
  if (!iso) return 'nunca abriu o app';
  const d = new Date(iso);
  return `abriu em ${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} as ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

export function UsuariosApp() {
  const [lista, setLista] = useState<UsuarioApp[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Id da conta cujo papel esta sendo gravado agora — trava so aquela linha. */
  const [gravando, setGravando] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLista(await api.get<UsuarioApp[]>('/api/app-usuarios'));
      setErro(null);
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui carregar a lista agora.');
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  async function trocarPapel(u: UsuarioApp) {
    const novo = u.papel === 'administrador' ? 'usuario' : 'administrador';
    setGravando(u.id);
    setErro(null);
    try {
      const alt = await api.patch<{ id: number; papel: UsuarioApp['papel'] }>(
        `/api/app-usuarios/${u.id}/papel`,
        { papel: novo },
      );
      // ⚠️ **Atualiza SO a linha.** Reler a lista aqui a reordenava (administrador vai
      // pro topo no servidor), a linha recem-promovida saltava e outra pessoa ocupava
      // o lugar embaixo do dedo — num celular isso e' pior ainda que no mouse. Ver o
      // comentario longo em `configuracoes/UsuariosAppCel.tsx`.
      setLista((atual) => atual?.map((x) => (x.id === alt.id ? { ...x, papel: alt.papel } : x)) ?? atual);
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui mudar o papel.');
    } finally {
      setGravando(null);
    }
  }

  if (!lista && !erro) {
    return <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginTop: 26 }}>Carregando...</p>;
  }

  return (
    <div>
      {erro && (
        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '11px 14px', fontSize: 13.5, lineHeight: 1.5 }}>
          {erro}
        </div>
      )}

      {lista?.map((u) => {
        const admin = u.papel === 'administrador';
        return (
          <div
            key={u.id}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', backgroundColor: '#fff', borderBottom: '1px solid #e7e5e4' }}
          >
            {admin
              ? <ShieldCheck size={20} style={{ color: COBRE, flexShrink: 0 }} />
              : <UserRound size={20} style={{ color: '#9ca3af', flexShrink: 0 }} />}

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {u.nome}
              </div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                {telefoneBonito(u.telefone)} · {quando(u.ultimo_acesso_em)}
              </div>
              {/* O papel vai ESCRITO, nao so na cor do icone — verde/vermelho nao
                  servem de sinal nesta casa, e "cinza x cobre" tambem nao se le. */}
              <div style={{ fontSize: 12.5, color: admin ? COBRE : '#9ca3af', marginTop: 2, fontWeight: admin ? 700 : 400 }}>
                {admin ? 'Administrador' : 'Usuario'}
              </div>
            </div>

            <button
              type="button"
              role="switch"
              aria-checked={admin}
              aria-label={`Administrador — ${u.nome}`}
              disabled={gravando === u.id}
              onClick={() => void trocarPapel(u)}
              style={{
                width: 52, height: 31, borderRadius: 999, flexShrink: 0,
                backgroundColor: admin ? COBRE : '#d6d3d1',
                opacity: gravando === u.id ? 0.45 : 1,
                position: 'relative', transition: 'background-color .15s',
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: admin ? 24 : 3,
                width: 25, height: 25, borderRadius: 999, backgroundColor: '#fff',
                transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
              }} />
            </button>
          </div>
        );
      })}

      {/* O que o interruptor faz, e o que ele NAO faz. Sem esta linha, "Administrador"
          num app de loja le como "pode tudo". */}
      <div style={{ padding: '14px 16px 22px', fontSize: 12.5, color: '#6b7280', lineHeight: 1.55 }}>
        Administrador enxerga a opcao <b>Entrar como loja</b> no menu do aplicativo. Isso nao da
        acesso nenhum ao sistema da loja: para entrar de fato ainda e preciso o login do sistema,
        no aparelho autorizado.
      </div>
    </div>
  );
}
